import { zodResolver } from '@hookform/resolvers/zod';
import { FormField, FormSubmitButton } from '@pm/forms';
import { useCreateMessageMutation } from '@pm/graphql';
import { TextareaField, useToast } from '@pm/ui';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { detectUserErrors } from '../../modules/errors/errors';
import { Attachment } from '../Attachments';
import { AttachmentUploaderField } from '../AttachmentUploader/AttachmentUploaderField';

type ReplyMessageFormProps = {
  toId: string;
  userId: string;
  messageId: string;
  subject: string;
};

const ReplyMessageForm = ({
  toId,
  userId,
  messageId,
  subject,
}: ReplyMessageFormProps) => {
  const [send] = useCreateMessageMutation();
  const { t } = useTranslation('messages');
  const { positive, negative } = useToast();

  const schema = z.object({
    content: z.string().min(1, t('MessageRequired')),
    attachments: z
      .custom<Attachment[]>()
      .refine(
        (attachments) => attachments.every((attachment) => !!attachment.blob),
        t('AttachmentsUploadingError'),
      ),
  });

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      content: '',
      attachments: [] as Attachment[],
    },
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    try {
      const { data: sendMessageData } = await send({
        variables: {
          input: {
            senderId: userId,
            recipientId: toId,
            parentId: messageId,
            subject: subject,
            content: data.content,
            attachments: data.attachments.map(
              (attachment) => attachment.blob?.signed_id ?? '',
            ),
          },
        },
        refetchQueries: ['GetConversation'],
        awaitRefetchQueries: true,
      });

      detectUserErrors(
        sendMessageData,
        (response) => response.createMessage?.errors,
      );

      methods.reset();
      positive({ content: t('MessageSent') });
    } catch {
      negative({
        title: t('Errors.SendMessage.Title'),
        content: t('Errors.SendMessage.Instructions'),
      });
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className="space-y-m">
          <FormField
            name="content"
            label={t('Reply')}
            placeholder={t('Respond')}
            Component={TextareaField}
          />
          <Controller
            control={methods.control}
            name="attachments"
            render={({ field, fieldState }) => (
              <AttachmentUploaderField
                label={t('Attachments') || 'Attachments'}
                name="attachments"
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
                placeholder={t('Drag') || ''}
              />
            )}
          />
          <div className="flex">
            <FormSubmitButton width="full">{t('Send')}</FormSubmitButton>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export { ReplyMessageForm };
